import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Chip, Skeleton, Typography } from '@mui/material';
import type { IncidentRow, SloBreachRow } from './types';

type Props = {
  incidents: IncidentRow[] | undefined;
  breaches: SloBreachRow[] | undefined;
  incidentsLoading: boolean;
  breachesLoading: boolean;
  incidentsError: Error | null;
  breachesError: Error | null;
};

export default function AlertsStrip({
  incidents,
  breaches,
  incidentsLoading,
  breachesLoading,
  incidentsError,
  breachesError,
}: Props) {
  const openIncidents = incidents || [];
  const openBreaches = breaches || [];
  const hasAlerts = openIncidents.length > 0 || openBreaches.length > 0;

  if (incidentsLoading || breachesLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={48} />
      </Box>
    );
  }

  if (incidentsError && breachesError) return null;

  if (!hasAlerts) {
    return (
      <Alert severity="success" sx={{ mb: 2 }} icon={false}>
        <Typography variant="body2">
          No open incidents · No active SLO breaches
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'warning.light', borderRadius: 1, bgcolor: 'warning.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">Active alerts</Typography>
        {openIncidents.length > 0 && (
          <Chip size="small" color="error" label={`${openIncidents.length} incident${openIncidents.length === 1 ? '' : 's'}`} />
        )}
        {openBreaches.length > 0 && (
          <Chip size="small" color="warning" label={`${openBreaches.length} SLO breach${openBreaches.length === 1 ? '' : 'es'}`} />
        )}
      </Box>

      {openIncidents.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>Incidents</Typography>
            <Typography component={Link} to="/system" variant="caption" color="primary">
              View all
            </Typography>
          </Box>
          {openIncidents.slice(0, 5).map((inc) => (
            <Typography key={inc._id} variant="body2" sx={{ fontSize: 13 }}>
              <Chip size="small" label={inc.severity} sx={{ mr: 1, height: 20, fontSize: 11 }} />
              {inc.title}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {new Date(inc.createdAt).toLocaleString()}
              </Typography>
            </Typography>
          ))}
        </Box>
      )}

      {openBreaches.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>SLO breaches</Typography>
            <Typography component={Link} to="/system" variant="caption" color="primary">
              View all
            </Typography>
          </Box>
          {openBreaches.slice(0, 5).map((b) => (
            <Typography key={b._id} variant="body2" sx={{ fontSize: 13 }}>
              {String(b.policy?.name || b.policyId)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {b.at ? new Date(b.at).toLocaleString() : '—'}
                {b.value != null && b.threshold != null && (
                  <> · {b.value} vs {b.threshold}</>
                )}
              </Typography>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
